import React, { useState, useEffect } from "react";
import MainDashboardLayout from "../../../../Layout/MainDashboardLayout";
import GRISidebar from "../../../../Layout/GRISidebar";
import styles from "../../../../styles/gri/general.module.css";
import { Box, Button, Typography, Alert, Snackbar } from "@mui/material";
import EastIcon from "@mui/icons-material/East";
import { put, get } from "../../../../utils/api";
import { useNavigate, useParams } from "react-router-dom";
import DynamicTable from "../../../../Components/Common/DynamicTable";
import { restructureData } from "../../../../utils/restructureData";
import { validateNumber } from "../../../../utils/validateFields";
import InputField from "../../../../Components/Masters/InputField";

import ReviewBox from "../../../../Components/Common/ReviewBox";
import { currentYear } from "../../../../utils/generateYearRanges";
import { fixedPercentage } from "../../../../utils/common";

const GRI205_1 = ({ hide }) => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [disabled, setDisabled] = useState(false);
  const [errors, setErrors] = useState({});
  const [snackOpen, setSnackOpen] = useState({
    open: false,
    message: "",
    severity: "",
  });
  //  const [formData, setFormData] = useState({});
  const [formData, setFormData] = useState({
    operationsAssessedForCorruption: {
      numberOfEmployees: "",
      total: "",
      percentage: "",
    },
  });
  const [griReview, setGriReview] = useState(() => JSON.parse(localStorage.getItem("griReview")));

  const inputData1 = [
    {
      fieldName: "significantCorruptionRisks",
      title:
        "b. Identify the significant risks related to corruption that have been identified through the risk assessment process?",
      fields: [
        {
          fieldName: "riskCorruption",
          title: "Risks related to corruption",
          type: "text",
          placeholder: "Enter Text",
        },
      ],
    },
  ];

  const [tables, setTables] = React.useState([]);
  const [tabErrors, setTabErrors] = useState({});
  const [data, setData] = useState({});

  const inputData = [
    {
      fieldName: "numberOfEmployees",
      title: "Number of operations assessed",
      type: "number",
      placeholder: "Enter Number",
      width: "25rem",
    },
    {
      fieldName: "total",
      title: "Total operations",
      type: "number",
      placeholder: "Enter Number",
      width: "25rem",
    },
  ];

  const calculate = () => {
    if (!formData.operationsAssessedForCorruption) return "";

    const { numberOfEmployees, total } =
      formData.operationsAssessedForCorruption;

    if (
      !numberOfEmployees ||
      !total ||
      errors.numberOfEmployees ||
      errors.total ||
      total === 0
    ) {
      return ""; // Return empty if invalid
    }

    return (fixedPercentage(Number(numberOfEmployees) / Number(total)) * 100);
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await get(`/gri/get?type=GRI200&Id=${id}`);
        const data1 = response.data.data.GRI200 || {};
        if (response?.data?.data?.year < currentYear - 1)
          setDisabled(true);
        setData(data1);
        setFormData({
          operationsAssessedForCorruption: {
            numberOfEmployees:
              data1?.operationsAssessedForCorruption?.numberOfEmployees || "",
            total: data1?.operationsAssessedForCorruption?.total || "",
            percentage:
              data1?.operationsAssessedForCorruption?.percentage || "",
          },
        });
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    };

    fetchData();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (
      name !== "referencedAgreementsTypes" &&
      value !== "" &&
      !validateNumber(value)
    )
      return;

    setFormData((prev) => ({
      ...prev,
      operationsAssessedForCorruption: {
        ...prev.operationsAssessedForCorruption,
        [name]: value,
      },
    }));
  };

  // 🔥 **Use `useEffect` to clear validation dynamically**
  useEffect(() => {
    if (!formData.operationsAssessedForCorruption) return; // ✅ Prevents undefined error

    const { numberOfEmployees, total } =
      formData.operationsAssessedForCorruption || {};

    setErrors((prevErrors) => {
      const newErrors = { ...prevErrors };

      if (Number(numberOfEmployees) > Number(total)) {
        newErrors.numberOfEmployees =
          "Value should not be greater than Total operations.";
      } else {
        delete newErrors.numberOfEmployees;
      }

      if (Number(total) < Number(numberOfEmployees)) {
        newErrors.total =
          "Value should not be less than Number of operations assessed.";
      } else {
        delete newErrors.total;
      }

      return newErrors;
    });
  }, [
    formData?.operationsAssessedForCorruption?.numberOfEmployees,
    formData?.operationsAssessedForCorruption?.total,
  ]);

  const validateForm = () => {
    const newErrors = {};
    inputData.forEach((field) => {
      if (formData?.operationsAssessedForCorruption?.[field.fieldName] === "") {
        newErrors[field.fieldName] = "Required";
      }
    });
    setErrors({ ...errors, ...newErrors });
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!disabled) {
      const isFormValid = validateForm();
      const percentage = calculate();

      if (!isFormValid || percentage === "") {
        setErrors((prev) => ({
          ...prev,
          percentage: "Percentage calculation is invalid.",
        }));
        return;
      }

      let transformedData = restructureData(tables);

      let formData2 = {
        ...formData,
        operationsAssessedForCorruption: {
          ...formData.operationsAssessedForCorruption,
          percentage: percentage,
        },
      };

      const payload = {
        type: "GRI200",
        submitted205_1: true,
        ...transformedData,
        ...formData2,
      };

      try {
        const token = sessionStorage.getItem("token");
        if (!token) {
          throw new Error("Authentication token is missing.");
        }
        await put(`/gri/update?Id=${id}`, payload);
        navigate(`/gri/economic-performance/205-2/${id}`);
      } catch (error) {
        setSnackOpen({
          open: true,
          message: "Error updating data. Please try again.",
          severity: "error",
        });
      }
    } else {
      navigate(`/gri/economic-performance/205-2/${id}`);
    }
  };

  const renderContent = ({ hide }) => (
    <>
      {!hide &&<p className={styles.section_heading} style={{marginBottom: hide ? "-1rem" : "0"}}>205 : Anti-corruption 2016</p>}
      <p className={styles.main_heading} style={{marginBottom: hide ? "-1rem" : "0"}}>
        205-1 : Operations assessed for risks related to corruption
      </p>

      {/* Section A */}

      <Box sx={{ display: "flex", gap: 1 }}>
        {/* <p className={styles.ques_heading}>a.</p> */}

        <p className={styles.ques_heading}>
          a. How many operations have undergone assessment for risks related to
          corruption, and what percentage does this represent of your total
          operations?
        </p>
      </Box>
      <Box sx={{ display: "flex", gap: 3, ml: 2 }}>
        {inputData.map((el) => (
          <Box sx={{ width: el.width }}>
            <Typography sx={{ fontWeight: 500, fontSize: "15px" }}>
              {el.title}
            </Typography>
            <InputField
              placeholder={el.placeholder || "Enter Text"}
              fieldName={el.fieldName}
              value={
                formData?.operationsAssessedForCorruption?.[el.fieldName] || ""
              }
              handleChange={handleChange}
              errors={!!errors[el.fieldName]} // Check if there's an error for this field
              disabled={disabled}
            />

            {errors[el.fieldName] && (
              <p
                style={{
                  lineHeight: "1.2",
                  margin: "2px 0",
                  marginTop: "5px",
                  color: "#d34040",
                  fontSize: "10px",
                  fontStyle: "italic",
                }}
              >
                {errors[el.fieldName]}
              </p>
            )}
          </Box>
        ))}
        <Box>
          <Typography sx={{ fontWeight: 500, fontSize: "15px" }}>
            Percentage
          </Typography>
          <Typography sx={{ mt: 2, fontSize: "15px", ml: 1 }}>
            {/* {calculate()}% */}
            {calculate() !== "" ? `${calculate()}%` : "--"}
          </Typography>
        </Box>
      </Box>

      <DynamicTable
        inputData={inputData1}
        tables={tables}
        data={data}
        setTables={setTables}
        disabled={disabled}
        errors={tabErrors}
        setErrors={setTabErrors}
        hide={hide}
      />

      <Box sx={{ display: "flex", flexDirection: "column" }}>
        {/* Section B */}

        {/* Submit Button */}

        {/* Snackbar for success/error messages */}
        <Snackbar
          open={snackOpen.open}
          message={snackOpen.message}
          autoHideDuration={3000}
          anchorOrigin={{ vertical: "top", horizontal: "center" }}
          onClose={() => setSnackOpen({ ...snackOpen, open: false })}
        >
          <Alert
            severity={snackOpen.severity}
            variant="filled"
            sx={{ width: "100%" }}
          >
            {snackOpen.message}
          </Alert>
        </Snackbar>
      </Box>
    </>
  );

  return (
    <>
      {!hide ? (
        <MainDashboardLayout sidebar={<GRISidebar />}>
          {renderContent({ hide })}

          <>
            {!griReview ? (
              <Box sx={{ ml: "1rem", width: "98%" }}>
                <ReviewBox
                  url={"/gri"}
                  handleSubmit={handleSubmit}
                  url1={`/gri/economic-performance/205-2/${id}`}
                />
              </Box>
            ) : (
              <Box textAlign="right" sx={{ mt: 3 }}>
                <Button
                  variant="contained"
                  sx={{
                    bgcolor: "#1C486B",
                    textTransform: "none",
                    mt: 3,
                    mb: 5,
                  }}
                  onClick={handleSubmit}
                >
                  Save & Continue <EastIcon sx={{ ml: 2 }} />
                </Button>
              </Box>
            )}
          </>
        </MainDashboardLayout>
      ) : (
        renderContent({ hide })
      )}
    </>
  );
};

export default GRI205_1;
